import { useState } from 'react';
import { Alert, Flex, Loader } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import PageWithSidebar from 'src/UI/page-nav/PageWithSidebar';
import { useScopedMeetings } from 'src/meetings/hooks/useScopedMeetings';
import { meetingDetailPath, meetingsBasePath, type MeetingScope } from 'src/meetings/scope';
import { isPastEvent } from 'src/meetings/utils/date-utils';
import { AddExpectedAttendeesModal } from './AddExpectedAttendeesModal';
import { FamilyMeetingsList } from './FamilyMeetingsList';
import { GenerateMeetingNoteModal } from './GenerateMeetingNoteModal';

type Props = {
  scope: MeetingScope;
  section?: string;
};

const SECTIONS = {
  Upcoming: 'upcoming',
  Past: 'past',
} as const;

export function FamilyMeetingsPanel({ scope, section }: Props) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { events, isLoading, error } = useScopedMeetings(scope);
  const [participantsMeetingId, setParticipantsMeetingId] = useState<string | null>(null);
  const [noteMeetingId, setNoteMeetingId] = useState<string | null>(null);

  const basePath = meetingsBasePath(scope);

  if (section !== SECTIONS.Upcoming && section !== SECTIONS.Past) {
    const query = searchParams.toString();
    return <Navigate to={`${basePath}/${SECTIONS.Upcoming}${query ? `?${query}` : ''}`} replace />;
  }

  const isPastSection = section === SECTIONS.Past;

  const links = [
    { label: 'Upcoming', href: `${basePath}/${SECTIONS.Upcoming}` },
    { label: 'Past meetings', href: `${basePath}/${SECTIONS.Past}` },
  ];

  const sectionEvents = events.filter(event => isPastEvent(event) === isPastSection);

  const participantsMeeting = events.find(event => event.id === participantsMeetingId) ?? null;
  const noteMeeting = events.find(event => event.id === noteMeetingId) ?? null;

  function renderBody() {
    if (isLoading) {
      return (
        <Flex py="xxl" justify="center">
          <Loader />
        </Flex>
      );
    }

    if (error) {
      return (
        <Alert variant="light" color="yellow" icon={<IconAlertTriangle size={18} />}>
          Some meetings could not be loaded.
        </Alert>
      );
    }

    return (
      <FamilyMeetingsList
        meetings={sectionEvents}
        order={isPastSection ? 'desc' : 'asc'}
        emptyMessage={
          isPastSection ? 'No past meetings to show yet.' : 'No upcoming meetings to show yet.'
        }
        onOpenParticipants={setParticipantsMeetingId}
        onOpenDetails={eventId => navigate(meetingDetailPath(scope, eventId))}
        onGenerateMeetingNote={setNoteMeetingId}
      />
    );
  }

  return (
    <>
      <PageWithSidebar links={links} activeUrl={`${basePath}/${section}`} variant="profileV2">
        {renderBody()}
      </PageWithSidebar>
      {participantsMeeting && (
        <AddExpectedAttendeesModal
          opened
          onClose={() => setParticipantsMeetingId(null)}
          scope={scope}
          meeting={participantsMeeting}
        />
      )}
      {noteMeeting && (
        <GenerateMeetingNoteModal
          opened
          onClose={() => setNoteMeetingId(null)}
          scope={scope}
          meeting={noteMeeting}
        />
      )}
    </>
  );
}
